import { clearOpenPanelRef } from './utils/panelManager.js';
import { applySheets } from '../../../app/utils/styleCache.js';
import { PanelMixin }        from './utils/panelMixin.js';
import { ValueMixin }        from './utils/valueMixin.js';
import { DropdownRenderer }   from './renderers/dropdownRenderer.js';
import { CalendarRenderer }   from './renderers/calendarRenderer.js';
import { DatepickerRenderer } from './renderers/datepickerRenderer.js';
import { DaterangeRenderer }  from './renderers/daterangeRenderer.js';
import { TimerangeRenderer }  from './renderers/timerangeRenderer.js';
import { TextRenderer }       from './renderers/textRenderer.js';

export class AppInput extends HTMLElement {
    static get observedAttributes() {
        return ['type', 'value', 'placeholder', 'disabled', 'multiple', 'label', 'icon',
                'empty-option', 'empty-label', 'max-display', 'min', 'max',
                'min-time', 'max-time', 'mode', 'minute-step', 'strict-range'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.isMounted      = false;
        this.options        = [];
        this.value          = null;
        this.isOpen         = false;
        this.outsideHandler = null;

        this.calViewDate = null;
        this.dateInput   = null;
        this.timeInput   = null;

        this.rangeMonth  = null;
        this.rangeStart  = null;
        this.rangeEnd    = null;
        this.rangeHover  = null;

        this.timeFrom    = '08:00';
        this.timeTo      = '17:00';
        this.timeValue   = null;

        this.textInput   = null;
        this.panel       = null;
        this.trigger     = null;

        this._pendingSetValue = null;
    }

    connectedCallback() {
        this.shadowRoot.innerHTML = `
            <style>
                :host { display: block; position: relative; }
            </style>
        `;
        applySheets(this.shadowRoot, ['components/shared/app-input/app-input.css']).then(() => {
            this.isMounted = true;
            this.render();
            requestAnimationFrame(() => this.setAttribute('loaded', ''));
            if (this.options.length > 0 || this.hasAttribute('empty-option')) {
                const type = this.getAttribute('type') || 'dropdown';
                if (type === 'dropdown' && this.panel) {
                    this.buildOptions(this.panel, this.hasAttribute('multiple'));
                    this.updateTriggerLabel(this.hasAttribute('multiple'));
                }
            }
            if (this._pendingSetValue !== null) {
                const { value, silent } = this._pendingSetValue;
                this._pendingSetValue = null;
                this.setValue(value, { silent });
            }
        });
    }

    attributeChangedCallback(name, oldVal, newVal) {
        if (!this.isMounted || oldVal === newVal) return;
        if (name === 'value') this.setValue(newVal, { silent: true });
        if (['disabled', 'type', 'label', 'icon', 'empty-option', 'empty-label'].includes(name)) this.render();
        if (name === 'max-display' && this.panel) {
            this.updateTriggerLabel(this.hasAttribute('multiple'));
        }
        if ((name === 'min' || name === 'max') && this.panel) {
            const type = this.getAttribute('type');
            if (type === 'datepicker') this.buildCalendar();
            if (type === 'daterange')  this.buildRangePanel();
        }
        if (['min-time', 'max-time', 'minute-step', 'strict-range'].includes(name)) {
            const inputType = this.getAttribute('type');
            if (inputType === 'timerange' || inputType === 'timepicker') this.render();
        }
    }

    disconnectedCallback() {
        try { document.removeEventListener('click', this.outsideHandler); } catch (_) {}
        clearOpenPanelRef(this);
        this._destroyPortal();
    }

    render() {
        switch (this.getAttribute('type') || 'dropdown') {
            case 'dropdown':   this.renderDropdown();   break;
            case 'datepicker': this.renderDatepicker(); break;
            case 'daterange':  this.renderDaterange();  break;
            case 'timerange':  this.renderTimerange();  break;
            case 'timepicker': this.renderTimepicker(); break;
            case 'text':       this.renderText();       break;
            case 'textarea':   this.renderTextarea();   break;
            default:           this.renderDropdown();   break;
        }
    }
}

Object.assign(
    AppInput.prototype,
    PanelMixin,
    ValueMixin,
    DropdownRenderer,
    CalendarRenderer,
    DatepickerRenderer,
    DaterangeRenderer,
    TimerangeRenderer,
    TextRenderer,
);

customElements.define('app-input', AppInput);
